import React, { useContext, useEffect, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import {
  bool,
  string,
  shape,
  func
} from 'prop-types';
import _ from "lodash"; // eslint-disable-line

import { useStore } from '@thd-nucleus/experience-context';

import { ReservationContext } from '../../context/reservation-context';
import { DEFAULT_STORE_HOURS } from '../../constants/constants';
import { initFlatpickr } from '../../utils/flatpickr-utils';
import helper from '../../utils/helper';

import './date-time-picker.scss';

export const DateTimePicker = ({
  disabled,
  label,
  field,
  validationClasses,
  handleClick,
  handleChange,
  calendarEnabled
}) => {

  const [timeOptions, setTimeOptions] = useState([]);
  const { storeHours = DEFAULT_STORE_HOURS, storeId } = useStore();
  const [{ reservation }, dispatch] = useContext(ReservationContext); // NOSONAR

  const defaultOpts = initFlatpickr(storeId, storeHours, field === 'pickup' ? '' : reservation?.pickupDate);

  const timeField = `${field}Time`;
  const dateField = `${field}Date`;

  useEffect(() => {
    if (reservation[`${field}Date`]) {
      const newTimeOptions = field === 'pickup' ? (
        helper.getReservationTimeOptions({
          storeNbr: storeId,
          rentalStoreHours: storeHours,
          reservationDate: reservation[`${field}Date`]
        })
      ) : (
        helper.getEndingTimeOptions({
          rentalStoreHours: storeHours,
          reservationDate: reservation[`${field}Date`],
          pickupTime: _.isEqual(reservation?.pickupDate, reservation?.dropoffDate)
            ? reservation?.pickupTime
            : ''
        })
      );

      if (!_.isEqual(newTimeOptions, timeOptions)) {
        setTimeOptions(newTimeOptions);
      }
    } else {
      setTimeOptions([]);
    }
  }, [reservation]);

  const mapOptions = () => {
    return timeOptions.map((choice) => (
      <option
        key={choice.value}
        value={choice.value}
      >
        {choice.name}
      </option>
    ));
  };

  return (
    <div className="date-time-picker" data-testid={`${field}-date-time-picker`}>
      <div className="date-time-label" data-testid="date-time-label">{label}</div>
      <div className="date-time-inputs">
        <div
          className={`form-input ${validationClasses[dateField]}`}
          data-testid={`${field}-date-input`}
        >
          <Flatpickr
            className={`form-input__field date-selector ${validationClasses[dateField]}`}
            disabled={disabled}
            name={dateField}
            data-testid={dateField}
            placeholder="Date"
            options={{ ...defaultOpts, clickOpens: calendarEnabled }}
            onClick={(input) => {
              input.stopPropagation();
              handleClick(input.target.name);
            }}
            onChange={(dateObject, dateString) => handleChange({ value: dateString, field: dateField })}
            value={reservation[dateField]}
          />
        </div>
        <div className="glex-gap" />
        <div
          className={`form-input ${validationClasses[timeField]}`}
          data-testid={`${field}-time-input`}
        >
          <select
            className={`form-input__field time-selector ${validationClasses[timeField]}`}
            disabled={disabled}
            name={timeField}
            data-testid={timeField}
            onClick={(input) => {
              input.stopPropagation();
              handleClick(input.target.name);
            }}
            onChange={(input) => handleChange({ value: input.target.value, field: timeField })}
            value={reservation[timeField]}
          >
            <option value="" disabled hidden>Time</option>
            {mapOptions()}
          </select>
        </div>
      </div>
    </div>
  );
};

DateTimePicker.propTypes = {
  // eslint-disable-next-line react/require-default-props
  disabled: bool,
  label: string.isRequired,
  field: string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  validationClasses: shape({}).isRequired,
  handleClick: func.isRequired,
  handleChange: func.isRequired,
  calendarEnabled: bool
};

DateTimePicker.defaultProps = {
  disabled: false,
  calendarEnabled: true
};
